import { useState } from 'react';
import {
  packKindMeta,
  type PackItem,
  type TravelPack,
} from '@/lib/travelPack';

// ─────────────────────────────────────────────────────────────
// Carte « Pack voyage » dans le chat IA.
// Résumé du pack + détail complet + réservation en un clic.
// ─────────────────────────────────────────────────────────────

interface Props {
  pack: TravelPack;
  busy?: boolean;
  reserved?: boolean;
  onReserve: () => void;
}

function money(value: number, currency: string): string {
  return `${(Number(value) || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${currency}`;
}

function frDate(iso: string): string {
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  } catch {
    return iso;
  }
}

function ProgramTimeline({ pack }: { pack: TravelPack }) {
  const days = pack.program || [];
  if (days.length === 0) return null;
  return (
    <div className="px-4 py-3 border-t border-background-200/60">
      <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-foreground-400 mb-2.5">
        Programme jour par jour
      </p>
      <div className="space-y-3">
        {days.map((day) => (
          <div key={day.day} className="flex gap-3">
            <div className="flex flex-col items-center flex-shrink-0">
              <span className="w-7 h-7 rounded-full bg-accent-100 text-accent-800 text-[11px] font-bold flex items-center justify-center">
                J{day.day}
              </span>
              {day.day !== days[days.length - 1].day && <span className="flex-1 w-px bg-background-200/70 my-1"></span>}
            </div>
            <div className="min-w-0 pb-1">
              <p className="text-[12px] font-semibold text-foreground-900 leading-snug">{day.title}</p>
              <p className="text-[10px] text-foreground-400">{frDate(day.date)}</p>
              <ul className="mt-1 space-y-0.5">
                {day.lines.map((line) => (
                  <li key={line} className="text-[11px] text-foreground-600 flex items-start gap-1.5">
                    <i className="ri-checkbox-blank-circle-fill text-[4px] mt-1.5 text-accent-500 flex-shrink-0"></i>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ItemRow({ item }: { item: PackItem }) {
  const meta = packKindMeta(item.kind);
  return (
    <div className="flex items-start gap-2.5 py-2.5">
      <span className="w-8 h-8 rounded-lg bg-background-50 text-foreground-600 flex items-center justify-center flex-shrink-0">
        <i className={`${meta.icon} text-sm`}></i>
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-accent-700">{meta.label}</span>
          {item.merchantName && (
            <span className="text-[10px] text-foreground-400 truncate">· {item.merchantName}</span>
          )}
        </div>
        <p className="text-sm font-semibold text-foreground-900 leading-snug">{item.name}</p>
        <p className="text-[11px] text-foreground-500 mt-0.5">{item.detail}</p>
      </div>
      <span className="text-xs font-bold text-foreground-900 whitespace-nowrap flex-shrink-0">
        {item.price > 0 ? money(item.price, item.currency) : 'Sur devis'}
      </span>
    </div>
  );
}

export default function TravelPackCard({ pack, busy = false, reserved = false, onReserve }: Props) {
  const [open, setOpen] = useState(false);
  const usedPart = pack.budget > 0 ? Math.min(100, Math.round((pack.total / pack.budget) * 100)) : 0;

  return (
    <>
      <div className="mt-3 rounded-xl border border-accent-200/70 bg-background-50 overflow-hidden">
        <div className="px-3.5 py-3 bg-accent-50/70 border-b border-accent-200/50">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-accent-800 flex items-center gap-1">
                <i className="ri-map-2-line"></i>Pack week-end
              </p>
              <p className="text-sm font-bold font-heading text-foreground-950 mt-0.5 truncate">
                {pack.destination}
              </p>
              <p className="text-[11px] text-foreground-600 mt-0.5">
                {frDate(pack.checkIn)} → {frDate(pack.checkOut)} · {pack.nights} nuit{pack.nights > 1 ? 's' : ''} · {pack.guests} pers.
              </p>
              {pack.days > 1 && (
                <p className="text-[10px] text-foreground-400 mt-0.5 flex items-center gap-1">
                  <i className="ri-calendar-2-line"></i>{pack.days} jours · programme détaillé
                </p>
              )}
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-[10px] uppercase tracking-wide text-foreground-400 font-semibold">Total</p>
              <p className="text-base font-bold font-heading text-foreground-950 whitespace-nowrap">
                {money(pack.total, pack.currency)}
              </p>
              {pack.budget > 0 && (
                <p className={`text-[10px] font-semibold ${pack.withinBudget ? 'text-accent-700' : 'text-red-500'}`}>
                  {pack.withinBudget ? 'dans le budget' : 'hors budget'}
                </p>
              )}
            </div>
          </div>

          {pack.budget > 0 && (
            <div className="mt-2.5">
              <div className="h-1.5 rounded-full bg-background-200/70 overflow-hidden">
                <div
                  className={`h-full rounded-full ${pack.withinBudget ? 'bg-accent-500' : 'bg-red-400'}`}
                  style={{ width: `${usedPart}%` }}
                ></div>
              </div>
              <div className="flex items-center justify-between mt-1 text-[10px] text-foreground-500">
                <span>{pack.items.length} prestation{pack.items.length > 1 ? 's' : ''}</span>
                <span>Budget {money(pack.budget, pack.currency)}</span>
              </div>
            </div>
          )}
        </div>

        <div className="px-3.5 py-1 divide-y divide-background-200/50">
          {pack.items.slice(0, 3).map((item) => (
            <ItemRow key={`${item.kind}-${item.entityId}`} item={item} />
          ))}
          {pack.items.length > 3 && (
            <p className="py-2 text-[11px] text-foreground-400">
              + {pack.items.length - 3} autre{pack.items.length - 3 > 1 ? 's' : ''} prestation{pack.items.length - 3 > 1 ? 's' : ''}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 px-3.5 py-3 border-t border-background-200/60 bg-background-100/40">
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-background-50 border border-background-200/70 text-[11px] font-semibold text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-eye-line text-xs"></i>
            Voir le détail
          </button>
          {reserved ? (
            <span className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-accent-100 text-accent-900 text-[11px] font-semibold whitespace-nowrap">
              <i className="ri-check-double-line text-xs"></i>
              Pack réservé
            </span>
          ) : (
            <button
              type="button"
              onClick={onReserve}
              disabled={busy}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-accent-500 text-background-50 text-[11px] font-bold hover:bg-accent-600 disabled:opacity-60 transition-colors cursor-pointer whitespace-nowrap"
            >
              {busy ? <i className="ri-loader-4-line animate-spin text-xs"></i> : <i className="ri-check-double-line text-xs"></i>}
              {busy ? 'Réservation…' : 'Tout réserver'}
            </button>
          )}
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-[75] flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)}></div>
          <div className="relative w-full max-w-lg bg-background-50 rounded-xl overflow-hidden max-h-[88vh] flex flex-col">
            <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-background-200/70 flex-shrink-0">
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-accent-700">Pack complet</p>
                <h3 className="text-base font-bold font-heading text-foreground-950 truncate">{pack.destination}</h3>
                <p className="text-[11px] text-foreground-500">
                  {frDate(pack.checkIn)} → {frDate(pack.checkOut)} · {pack.nights} nuit{pack.nights > 1 ? 's' : ''} · {pack.days} jour{pack.days > 1 ? 's' : ''}
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="w-8 h-8 rounded-full text-foreground-500 hover:bg-background-100 flex items-center justify-center flex-shrink-0 cursor-pointer"
                aria-label="Fermer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="overflow-y-auto flex-1 px-4 py-2 divide-y divide-background-200/50">
              {pack.items.map((item) => (
                <ItemRow key={`${item.kind}-${item.entityId}`} item={item} />
              ))}
            </div>

            <ProgramTimeline pack={pack} />

            {pack.notes.length > 0 && (
              <div className="px-4 py-2.5 border-t border-background-200/60 bg-secondary-50/60">
                {pack.notes.map((note) => (
                  <p key={note} className="text-[11px] text-secondary-900 flex items-start gap-1.5 py-0.5">
                    <i className="ri-information-line mt-0.5 flex-shrink-0"></i>
                    <span>{note}</span>
                  </p>
                ))}
              </div>
            )}

            <div className="px-4 py-3 border-t border-background-200/70 flex-shrink-0">
              <div className="flex items-center justify-between mb-2.5">
                <span className="text-xs text-foreground-600">Total du pack</span>
                <span className="text-lg font-bold font-heading text-foreground-950">{money(pack.total, pack.currency)}</span>
              </div>
              <p className="text-[10px] text-foreground-400 leading-relaxed mb-3">
                Chaque prestation est réservée auprès de son commerce : logement, guide, chauffeur et table locale
                reçoivent chacun leur notification de réservation.
              </p>
              {reserved ? (
                <div className="w-full py-2.5 rounded-full bg-accent-100 text-accent-900 text-sm font-semibold text-center">
                  <i className="ri-check-double-line mr-1.5"></i>Pack réservé
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => { setOpen(false); onReserve(); }}
                  disabled={busy}
                  className="w-full py-2.5 rounded-full bg-accent-500 text-background-50 text-sm font-bold hover:bg-accent-600 disabled:opacity-60 transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  {busy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-double-line"></i>}
                  {busy ? 'Réservation en cours…' : 'Réserver tout le pack'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}